import { doApi } from './apiClient';

export interface OwnedCardEntry {
  card_id: number;
  quantity: number;
}

export const apiGetMyOwnedCards = (): Promise<OwnedCardEntry[]> =>
  doApi<OwnedCardEntry[]>('api/user/cards/owned', 'GET');

export const apiGetMyCardAccount = (): Promise<{ credits: number }> =>
  doApi<{ credits: number }>('api/card-accounts/me', 'GET');

export interface CreditHistoryEntry {
  id: number;
  amount: number;
  notes: string | null;
  created_at: string;
  granted_by: { id: number; name: string } | null;
}

export const apiGetMyCreditHistory = (): Promise<CreditHistoryEntry[]> =>
  doApi<CreditHistoryEntry[]>('api/user/credits/history', 'GET');

export interface ActivePack {
  id: number;
  name: string;
  cost: number;
  cards_per_pack: number;
  is_active: boolean;
  expansion: {
    id: number;
    name: string;
    expansion_path: string;
    notes: string | null;
  };
}

export const apiGetActivePacks = (): Promise<ActivePack[]> =>
  doApi<ActivePack[]>('api/packs/active', 'GET');

export interface PackOpenCard {
  id: number;
  rarity: string;
  fronte_path: string | null;
  retro_path: string | null;
  wrestlers: { id: number; name: string; avatar_path: string }[];
}

export interface PackOpenResult {
  cards: PackOpenCard[];
  credits_remaining: number;
  already_owned: number[];
}

export const apiOpenPack = (packId: number): Promise<PackOpenResult> =>
  doApi<PackOpenResult>(`api/packs/${packId}/open`, 'POST');

export interface DailyClaimStatus {
  can_claim: boolean;
  next_claim_at: string | null;
  credits_to_claim: number;
}

export interface DailyClaimResult {
  credits_given: number;
  credits_remaining: number;
  next_claim_at: string;
}

export const apiGetDailyClaimStatus = (): Promise<DailyClaimStatus> =>
  doApi<DailyClaimStatus>('api/user/daily-claim/status', 'GET');

export const apiClaimDailyCredits = (): Promise<DailyClaimResult> =>
  doApi<DailyClaimResult>('api/user/daily-claim/claim', 'POST');

export interface LeaderboardWrestler {
  id: number;
  name: string;
  avatar_path: string | null;
}

export interface LeaderboardEntry {
  rank: number;
  username: string;
  nickname_forum: string | null;
  score: number;
  unique_cards: number;
  common_count: number;
  rare_count: number;
  epic_count: number;
  legendary_count: number;
  total_copies: number;
  wrestlers: LeaderboardWrestler[];
}

export const apiGetLeaderboard = (): Promise<{ data: LeaderboardEntry[] }> =>
  doApi<{ data: LeaderboardEntry[] }>('api/cards/leaderboard', 'GET');

export const apiGetLeaderboardByExpansion = (expansionId: number): Promise<{ data: LeaderboardEntry[] }> =>
  doApi<{ data: LeaderboardEntry[] }>(`api/cards/leaderboard/expansion/${expansionId}`, 'GET');

export interface RandomPodiumExpansion {
  id: number;
  name: string;
  expansion_path: string;
}

export interface RandomPodiumResponse {
  type: 'expansion' | 'global';
  expansion: RandomPodiumExpansion | null;
  podium: LeaderboardEntry[];
}

export const apiGetRandomPodium = (): Promise<RandomPodiumResponse> =>
  doApi<RandomPodiumResponse>('api/cards/leaderboard/random-podium', 'GET');

// ─── User Profile ──────────────────────────────────────────────────────────────

export interface UserProfileWrestler {
  id: number;
  name: string;
  avatar_path: string | null;
}

export interface UserProfileCardAuthor {
  id: number;
  username: string;
  nickname_forum: string | null;
}

export interface UserProfileCard {
  id: number;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  fronte_path: string | null;
  author_user_id: number | null;
  author_name: string | null;
  author_user: UserProfileCardAuthor | null;
  reference_url: string | null;
  reference_string: string | null;
  reference_data: string | null;
  wrestlers: UserProfileWrestler[];
  expansions: { id: number; name: string }[];
}

export interface UserProfileOwnedCard {
  card_id: number;
  quantity: number;
  card: UserProfileCard | null;
}

export interface UserProfileStats {
  unique_cards: number;
  total_copies: number;
  legendary_count: number;
  total_catalog_cards: number;
  catalog_per_expansion: Record<string, number>;
}

export interface UserProfile {
  username: string;
  nickname_forum: string | null;
  wrestlers: UserProfileWrestler[];
  owned_cards: UserProfileOwnedCard[];
  stats: UserProfileStats;
}

export const apiGetUserProfile = (username: string): Promise<UserProfile> =>
  doApi<UserProfile>(`api/users/${username}/profile`, 'GET');
